import { useState } from "react";

interface TimelineEvent {
  id: string;
  time: string;
  date: string;
  action: string;
  operator: string;
  role: string;
  note: string;
  status: "done" | "current" | "pending";
  attachments?: string[];
}

const TIMELINE_DATA: TimelineEvent[] = [
  {
    id: "e1",
    time: "09:15",
    date: "2026-04-01",
    action: "提交报装申请",
    operator: "张建国（用户）",
    role: "申请人",
    note: "通过网上营业厅提交新装申请，上传身份证、房产证等材料",
    status: "done",
    attachments: ["身份证_张建国.jpg", "房产证.pdf"],
  },
  {
    id: "e2",
    time: "14:30",
    date: "2026-04-02",
    action: "资料审核通过",
    operator: "李明（审核员）",
    role: "审核员",
    note: "材料齐全，信息核实无误，审核通过，进入报备流程",
    status: "done",
    attachments: ["审核意见书.pdf"],
  },
  {
    id: "e3",
    time: "10:00",
    date: "2026-04-03",
    action: "向主管部门报备",
    operator: "系统自动",
    role: "系统",
    note: "已向城市燃气主管部门提交报备申请，等待批复",
    status: "done",
  },
  {
    id: "e4",
    time: "09:00",
    date: "2026-04-05",
    action: "安排现场勘查",
    operator: "王芳（调度员）",
    role: "调度员",
    note: "已安排技术员张工于4月6日上午10:00上门勘查，请保持手机畅通",
    status: "current",
  },
  {
    id: "e5",
    time: "—",
    date: "预计 2026-04-08",
    action: "施工安装",
    operator: "待分配",
    role: "施工队",
    note: "等待现场勘查完成后，制定施工方案并安排施工",
    status: "pending",
  },
  {
    id: "e6",
    time: "—",
    date: "预计 2026-04-12",
    action: "竣工验收",
    operator: "待分配",
    role: "质检员",
    note: "施工完成后，质检人员上门验收，签署验收报告",
    status: "pending",
  },
  {
    id: "e7",
    time: "—",
    date: "预计 2026-04-15",
    action: "开户通气",
    operator: "待处理",
    role: "营业员",
    note: "验收合格后，建立用户档案，开通燃气供应",
    status: "pending",
  },
];

const roleColors: Record<string, string> = {
  "申请人": "bg-orange-100 text-orange-600",
  "审核员": "bg-teal-100 text-teal-600",
  "系统": "bg-gray-100 text-gray-500",
  "调度员": "bg-amber-100 text-amber-600",
  "施工队": "bg-indigo-100 text-indigo-600",
  "质检员": "bg-green-100 text-green-600",
  "营业员": "bg-pink-100 text-pink-600",
};

interface TimelinePanelProps {
  appId: string;
  applicant: string;
}

export default function TimelinePanel({ appId, applicant }: TimelinePanelProps) {
  const [expanded, setExpanded] = useState<string[]>(["e4"]);

  const toggleExpand = (id: string) => {
    setExpanded(prev => prev.includes(id) ? prev.filter(e => e !== id) : [...prev, id]);
  };

  const doneCount = TIMELINE_DATA.filter(e => e.status === "done").length;

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-sm font-bold text-gray-900">业务进度时间轴</h3>
          <p className="text-xs text-gray-400 mt-0.5">{appId} · {applicant}</p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-green-500"></div>
            <span className="text-gray-500">已完成 {doneCount} 步</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-[#E8531A] animate-pulse"></div>
            <span className="text-gray-500">进行中</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-gray-200"></div>
            <span className="text-gray-500">待处理</span>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="relative">
        {TIMELINE_DATA.map((event, idx) => {
          const isExpanded = expanded.includes(event.id);
          const isLast = idx === TIMELINE_DATA.length - 1;

          return (
            <div key={event.id} className="flex gap-4">
              {/* Left: dot + line */}
              <div className="flex flex-col items-center flex-shrink-0" style={{ width: 20 }}>
                <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 z-10 ${
                  event.status === "done" ? "bg-green-500" :
                  event.status === "current" ? "bg-[#E8531A]" :
                  "bg-gray-200"
                }`}>
                  {event.status === "done" ? (
                    <i className="ri-check-line text-white text-[10px]"></i>
                  ) : event.status === "current" ? (
                    <div className="w-2 h-2 rounded-full bg-white animate-pulse"></div>
                  ) : (
                    <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                  )}
                </div>
                {!isLast && (
                  <div className={`w-0.5 flex-1 mt-1 ${event.status === "done" ? "bg-green-200" : "bg-gray-100"}`} style={{ minHeight: 32 }} />
                )}
              </div>

              {/* Right: content */}
              <div className={`flex-1 pb-5 ${isLast ? "pb-0" : ""}`}>
                <button
                  onClick={() => toggleExpand(event.id)}
                  className="w-full text-left cursor-pointer"
                >
                  <div className={`rounded-xl p-3.5 border transition-all ${
                    event.status === "current" ? "border-[#E8531A]/30 bg-orange-50" :
                    event.status === "done" ? "border-green-100 bg-green-50/50" :
                    "border-gray-100 bg-gray-50/50"
                  }`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-sm font-semibold ${
                          event.status === "current" ? "text-[#E8531A]" :
                          event.status === "done" ? "text-gray-800" :
                          "text-gray-400"
                        }`}>{event.action}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${roleColors[event.role] || "bg-gray-100 text-gray-500"}`}>
                          {event.role}
                        </span>
                        {event.status === "current" && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#E8531A] text-white animate-pulse">
                            进行中
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                        <div className="text-right">
                          <div className="text-xs text-gray-500">{event.date}</div>
                          {event.time !== "—" && <div className="text-[10px] text-gray-400">{event.time}</div>}
                        </div>
                        <i className={`ri-arrow-down-s-line text-gray-400 text-sm transition-transform ${isExpanded ? "rotate-180" : ""}`}></i>
                      </div>
                    </div>

                    {/* Expanded content */}
                    {isExpanded && (
                      <div className="mt-3 pt-3 border-t border-gray-100/80">
                        <div className="flex items-start gap-2 mb-2">
                          <i className="ri-user-line text-gray-400 text-xs mt-0.5"></i>
                          <span className="text-xs text-gray-500">操作人：<span className="text-gray-700 font-medium">{event.operator}</span></span>
                        </div>
                        <div className="flex items-start gap-2 mb-2">
                          <i className="ri-file-text-line text-gray-400 text-xs mt-0.5"></i>
                          <span className="text-xs text-gray-600 leading-relaxed">{event.note}</span>
                        </div>
                        {event.attachments && event.attachments.length > 0 && (
                          <div className="flex items-start gap-2">
                            <i className="ri-attachment-line text-gray-400 text-xs mt-0.5"></i>
                            <div className="flex flex-wrap gap-1.5">
                              {event.attachments.map((att, i) => (
                                <span key={i} className="text-[10px] bg-white border border-gray-200 text-gray-600 px-2 py-1 rounded-lg flex items-center gap-1 cursor-pointer hover:border-[#E8531A]/30 hover:text-[#E8531A]">
                                  <i className="ri-file-line text-[10px]"></i>
                                  {att}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
